import {
  BaseModel,
  useInScopeModel,
} from '@cotera/client/v0/actions/framework';
import {
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework/watchable';
import type { QueryClient } from '@tanstack/react-query';
import type { UrlParamAdapter } from '@cotera/client/app/etc/url-param-watchable';
import { MultiKeyUrlParamWatchable } from '@cotera/client/app/etc/url-param-watchable';
import {
  RESOURCE_LIST_FETCH_STALE_MS,
  queryKeys,
} from '@cotera/client/app/hooks/query-cache-keys';
import { ResourceType } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';
import type { AgentListItem } from '../shell/app.model';
import { WatchablesFactory } from '../watchables/watchables-factory';

export type ListParamsState = {
  page: number;
  limit: number;
  search: string;
  sort: string | undefined;
  sortDir: 'asc' | 'desc';
};

export const DEFAULT_LIST_PARAMS: ListParamsState = {
  page: 1,
  limit: 100,
  search: '',
  sort: undefined,
  sortDir: 'asc',
};

function parsePage(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_LIST_PARAMS.page;
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 1
    ? Math.floor(n)
    : DEFAULT_LIST_PARAMS.page;
}

function parseLimit(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_LIST_PARAMS.limit;
  }
  const n = Number(s);
  return Number.isFinite(n)
    ? Math.min(100, Math.max(1, Math.floor(n)))
    : DEFAULT_LIST_PARAMS.limit;
}

function parseListParams(params: URLSearchParams): ListParamsState {
  return {
    page: parsePage(params.get('page') ?? ''),
    limit: parseLimit(params.get('limit') ?? ''),
    search: params.get('search') ?? DEFAULT_LIST_PARAMS.search,
    sort: (() => {
      const v = params.get('sort');
      return v !== null && v !== '' ? v : undefined;
    })(),
    sortDir:
      params.get('sortDir') === 'desc' ? 'desc' : DEFAULT_LIST_PARAMS.sortDir,
  };
}

function serializeListParams(s: ListParamsState): Record<string, string> {
  return {
    page: s.page === DEFAULT_LIST_PARAMS.page ? '' : String(s.page),
    limit: String(s.limit),
    search: s.search,
    sort: s.sort ?? '',
    sortDir: s.sortDir === 'asc' ? '' : s.sortDir,
  };
}

function listParamsEquality(a: ListParamsState, b: ListParamsState): boolean {
  return (
    a.page === b.page &&
    a.limit === b.limit &&
    a.search === b.search &&
    a.sort === b.sort &&
    a.sortDir === b.sortDir
  );
}

export type AgentsListData = {
  items: AgentListItem[];
  total: number;
};

export type AgentsListPageModelProps = {
  orgId: string;
  queryClient: QueryClient;
  watchablesFactory: WatchablesFactory;
  urlAdapter: UrlParamAdapter;
  initialSearchParams: URLSearchParams;
  client: TenantedClient;
};

function toAgentListItem(agent: {
  id: string;
  ownerId: string;
  agentKey: string;
  description: string | null;
  title?: string | null;
  folderId?: string | null;
  folderName?: string | null;
  folderScope?: 'user' | 'org' | 'external' | null;
  emoji?: string | null;
}): AgentListItem {
  const description = agent.description ?? null;
  const title =
    agent.title !== undefined &&
    agent.title !== null &&
    agent.title.trim() !== ''
      ? agent.title.trim()
      : null;
  return {
    id: agent.id,
    ownerId: agent.ownerId,
    title: title ?? agent.agentKey,
    description,
    folderId: agent.folderId ?? null,
    folderName: agent.folderName ?? null,
    folderScope: agent.folderScope ?? null,
    emoji: agent.emoji ?? null,
  };
}

function agentsDataEquality(
  a: AgentsListData | null,
  b: AgentsListData | null
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if (a.total !== b.total || a.items.length !== b.items.length) {
    return false;
  }
  for (let i = 0; i < a.items.length; i++) {
    if (
      a.items[i]?.id !== b.items[i]?.id ||
      a.items[i]?.title !== b.items[i]?.title ||
      a.items[i]?.description !== b.items[i]?.description ||
      a.items[i]?.folderId !== b.items[i]?.folderId ||
      a.items[i]?.folderName !== b.items[i]?.folderName ||
      a.items[i]?.folderScope !== b.items[i]?.folderScope ||
      a.items[i]?.emoji !== b.items[i]?.emoji
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Model for the agents list page. Owns URL-synced watchables for list params (page, limit,
 * search, sort, sortDir) and an event watchable that fetches agent list data.
 */
export class AgentsListPageModel extends BaseModel {
  private readonly _listParamsUrl: MultiKeyUrlParamWatchable<ListParamsState>;
  private readonly _listParams: ReadonlyWatchable<ListParamsState>;
  private readonly _agentsDataEventWatchable: ReadonlyEventWatchable<AgentsListData | null>;

  constructor(props: AgentsListPageModelProps) {
    super('agents-list-page');
    const initial = parseListParams(props.initialSearchParams);
    const adapter = props.urlAdapter;

    this._listParamsUrl = new MultiKeyUrlParamWatchable<ListParamsState>(
      ['page', 'limit', 'search', 'sort', 'sortDir'],
      {
        default: DEFAULT_LIST_PARAMS,
        parse: parseListParams,
        serialize: serializeListParams,
        equalityFn: listParamsEquality,
      },
      adapter,
      initial
    );

    this._listParams = this._listParamsUrl.asWatchable();

    const watchablesFactory = props.watchablesFactory;

    this._agentsDataEventWatchable = watchablesFactory.makeAgentsListWatchable<
      AgentsListData | null,
      { resourceType?: string }
    >({
      orgId: props.orgId,
      initialValue: null,
      equalityFn: agentsDataEquality,
      filter: (payload) => payload?.resourceType === 'agent',
      fn: async (_payload, get) => {
        const { page, limit, search, sort, sortDir } = get(this._listParams);
        const allItems: AgentListItem[] = [];
        let total = 0;
        for (let p = 1; p <= page; p++) {
          const offset = (p - 1) * limit;
          const out = await props.queryClient.fetchQuery({
            queryKey: queryKeys.resource.listPage({
              orgId: props.orgId,
              type: ResourceType.Agent,
              limit,
              offset,
              ...(search !== undefined && search !== '' && { search }),
              ...(sort !== undefined && sort !== '' && { sort, sortDir }),
            }),
            staleTime: RESOURCE_LIST_FETCH_STALE_MS,
            queryFn: async () =>
              Assert.assertOk(
                await props.client.resource.list({
                  type: ResourceType.Agent,
                  limit,
                  offset,
                  ...(search !== undefined && search !== '' && { search }),
                  ...(sort !== undefined && sort !== '' && { sort, sortDir }),
                })
              ),
          });
          if (out.type !== 'agent') {
            break;
          }
          total = out.total;
          const agents = out.items.map((agent) =>
            toAgentListItem({
              id: agent.id,
              ownerId: agent.ownerId,
              agentKey: agent.agentKey,
              description: agent.description,
              title: agent.title,
              folderId: agent.folderId ?? null,
              folderName: agent.folderName ?? null,
              folderScope: agent.folderScope ?? null,
              emoji: agent.emoji ?? null,
            })
          );
          allItems.push(...agents);
          if (out.items.length < limit) {
            break;
          }
        }
        return { items: allItems, total };
      },
    });
  }

  get listParamsWatchable(): ReadonlyWatchable<ListParamsState> {
    return this._listParams;
  }

  get agentsDataWatchable(): ReadonlyEventWatchable<AgentsListData | null> {
    return this._agentsDataEventWatchable;
  }

  /** Sync list params from URL and refetch agents data for the current params. */
  syncFromUrl(searchParams: URLSearchParams): void {
    this._listParamsUrl.syncFromUrl(searchParams);
    void this._agentsDataEventWatchable.refresh();
  }

  /** Set list params to the given page and take. Refetch is triggered by syncFromUrl when URL updates. */
  loadPage(page: number, take: number): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, page, limit: take });
  }

  /** Set search query; resets page to 1 and refetches with the updated params. */
  setSearch(search: string): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, search, page: 1 });
    void this._agentsDataEventWatchable.refresh();
  }

  /** Set sort key and direction; resets page to 1. Refetch is triggered by syncFromUrl when URL updates. */
  setSort(sort: string | undefined, sortDir: 'asc' | 'desc'): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, sort, sortDir, page: 1 });
  }

  dispose(): void {
    this._agentsDataEventWatchable.unsubscribe();
    this._listParamsUrl.dispose();
  }
}

export const useAgentsListPageModel = (): AgentsListPageModel => {
  const model = useInScopeModel(AgentsListPageModel);
  if (model === null) {
    throw new Error('AgentsListPageModel not found in scope');
  }
  return model;
};
